import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import ListGroup from "react-bootstrap/ListGroup";
import {
  fetchPortalPermitApp,
  fetchPortalCheckApp,
  fetchPortalSpectApp
} from "../../services/portalService";
import { useToast } from "../../contexts/ToastContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faFileLines, faLink } from "@fortawesome/free-solid-svg-icons";

const API_BASE_URL = process.env.REACT_APP_API_URL;

// The API returns attachments in several shapes depending on product/endpoint:
//   [{ link }]            — permit (Applicant.formatApplications) & check/spect fileLinks
//   ["12","13"] / [12,13] — raw document ids
//   "12,13"               — raw comma-separated column value
//   "https://…"           — externally hosted link (checkApplications.fileLink)
// Normalize all of them to [{ name, link }] so nothing silently renders empty.
const buildFile = (raw) => {
  if (raw === null || raw === undefined) return null;

  if (typeof raw === "object") {
    const link = raw.link || raw.url || raw.fileLink || null;
    if (!link) return null;
    return { name: raw.name || raw.fileName || null, link, id: raw.id ?? null };
  }

  const val = String(raw).trim();
  if (!val) return null;
  if (/^https?:\/\//i.test(val)) return { name: null, link: val, id: null };
  // Bare document id straight from the DB column.
  return { name: null, link: `${API_BASE_URL}/attachments/files/${val}`, id: val };
};

const normalizeFiles = (input) => {
  if (input === null || input === undefined || input === "") return [];

  let items = input;
  if (typeof items === "string") {
    const trimmed = items.trim();
    if (trimmed.startsWith("[")) {
      try {
        items = JSON.parse(trimmed);
      } catch (e) {
        items = trimmed.split(",");
      }
    } else {
      items = trimmed.split(",");
    }
  }
  if (!Array.isArray(items)) items = [items];

  return items.map(buildFile).filter(Boolean);
};

const dedupeFiles = (files) => {
  const seen = new Set();
  return files.filter((f) => {
    if (seen.has(f.link)) return false;
    seen.add(f.link);
    return true;
  });
};

const FileLinkList = ({ files, icon = faFilePdf, emptyText = "No files uploaded." }) => {
  if (!files || files.length === 0) {
    return <span className="text-muted small fst-italic">{emptyText}</span>;
  }

  return (
    <ListGroup variant="flush">
      {files.map((file, index) => {
        const isExternal = !file.id;
        return (
          <ListGroup.Item
            key={file.link || index}
            className="px-0 py-2 bg-transparent border-0 d-flex align-items-center"
          >
            <FontAwesomeIcon
              icon={isExternal ? faLink : icon}
              className={`me-2 fs-5 ${isExternal ? "text-secondary" : "text-danger"}`}
            />
            <a
              href={file.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none text-primary fw-semibold small text-truncate"
              title={file.name || file.link}
            >
              {file.name || `File ${index + 1}`}
            </a>
          </ListGroup.Item>
        );
      })}
    </ListGroup>
  );
};

const DetailItem = ({ label, value }) => (
  <div className="mb-3">
    <span className="small text-muted d-block mb-1 text-uppercase fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
      {label}
    </span>
    <span className="fw-semibold text-dark fs-6" style={{ whiteSpace: "pre-line" }}>
      {value || <em className="text-muted small fw-normal">Not provided</em>}
    </span>
  </div>
);

const parseMoney = (val) => {
  if (!val) return 0;
  if (typeof val === 'number') return val;
  const cleaned = val.toString().replace(/[^0-9.]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const formatMoney = (val) => {
  const num = parseMoney(val);
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function PortalAppDetails() {
  const { product, id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lockReason, setLockReason] = useState("");
  const [originalApp, setOriginalApp] = useState(null);

  useEffect(() => {
    loadApplicationDetails();
  }, [product, id]);

  const loadApplicationDetails = async () => {
    try {
      setLoading(true);
      setError("");
      
      let res;
      if (product === "permit") {
        res = await fetchPortalPermitApp(id);
      } else if (product === "check") {
        res = await fetchPortalCheckApp(id);
      } else if (product === "spect") {
        res = await fetchPortalSpectApp(id);
      } else {
        throw new Error("Invalid product type");
      }

      setOriginalApp(res.application);
      setLockReason(res.lockReason);
    } catch (err) {
      setError(err.message || "Failed to load application details.");
      showToast("Error loading application details", "error");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const s = status ? status.toLowerCase() : "";
    if (s.includes("approved") || s === "active") return <Badge bg="success" className="fs-6 px-3">Approved</Badge>;
    if (s.includes("reject") || s.includes("deny") || s.includes("not approved")) return <Badge bg="danger" className="fs-6 px-3">Rejected</Badge>;
    if (s.includes("review") || s.includes("pending")) return <Badge bg="warning" text="dark" className="fs-6 px-3">Under Review</Badge>;
    return <Badge bg="secondary" className="fs-6 px-3">{status || "Submitted"}</Badge>;
  };

  if (loading) {
    return (
      <div className="text-center my-5 py-5">
        <Spinner animation="border" role="status" variant="primary" className="mb-2" />
        <p className="text-muted">Loading application details...</p>
      </div>
    );
  }

  if (error && !originalApp) {
    return (
      <div>
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate("/portal/dashboard")}>Back to Dashboard</Button>
      </div>
    );
  }

  // Documents the applicant submitted with the request.
  // permit  → location.files ; check/spect → fileLinks (+ raw files fallback) and the
  // external fileLink column. Fallbacks keep the list working even if the API
  // response predates the fileLinks transform.
  const submittedFiles = dedupeFiles([
    ...normalizeFiles(originalApp.location?.files),
    ...normalizeFiles(originalApp.fileLinks),
    ...normalizeFiles(originalApp.files),
    ...normalizeFiles(originalApp.fileLink),
  ]);

  // Documents the reviewer/community issued back to the applicant.
  const reports = dedupeFiles([
    ...normalizeFiles(originalApp.standardReport),
    ...normalizeFiles(originalApp.reportLinks),
  ]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button variant="link" className="text-decoration-none p-0 text-secondary mb-2 small" onClick={() => navigate("/portal/dashboard")}>
            &larr; Back to Dashboard
          </Button>
          <h2 className="text-dark fw-bold mb-1">Application Summary</h2>
          <span className="text-muted">
            ID: #{id} &bull; Product: <span className="text-uppercase fw-semibold">{product}</span>
            {originalApp.createdAt && ` &bull; Submitted: ${new Date(originalApp.createdAt).toLocaleDateString("en-US", { year: 'numeric', month: 'short', day: 'numeric' })}`}
          </span>
        </div>
        <div>
          {originalApp && getStatusBadge(originalApp.status)}
        </div>
      </div>

      {lockReason && (
        <Alert variant="info" className="border-0 shadow-sm mb-4 bg-light text-dark">
          <h5 className="alert-heading small fw-bold mb-1">Editing Mode Locked</h5>
          <p className="mb-0 small text-muted">{lockReason}</p>
        </Alert>
      )}

      <Row>
        {/* Main Details Panel */}
        <Col lg={8}>
          {/* Permit Product Layout */}
          {product === "permit" && (
            <>
              {/* Applicant Details */}
              <Card className="shadow-sm border-0 mb-4 card-premium">
                <Card.Header className="bg-white border-bottom py-3">
                  <h5 className="mb-0 fw-semibold text-dark">Applicant Details</h5>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row>
                    <Col md={6}>
                      <DetailItem label="First Name" value={originalApp.firstName} />
                    </Col>
                    <Col md={6}>
                      <DetailItem label="Last Name" value={originalApp.lastName} />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <DetailItem label="Email Address" value={originalApp.email} />
                    </Col>
                    <Col md={6}>
                      <DetailItem label="Phone Number" value={originalApp.phoneNumber} />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <DetailItem label="Street Address" value={originalApp.address} />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <DetailItem label="Applicant Notes" value={originalApp.notes} />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Location & Work Details */}
              <Card className="shadow-sm border-0 mb-4 card-premium">
                <Card.Header className="bg-white border-bottom py-3">
                  <h5 className="mb-0 fw-semibold text-dark">Location & Work Details</h5>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row>
                    <Col md={6}>
                      <DetailItem label="Project Address" value={originalApp.location?.address} />
                    </Col>
                    <Col md={6}>
                      <DetailItem label="Permit Type" value={originalApp.location?.permitType} />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <DetailItem label="Cost of Construction" value={originalApp.location?.cost ? `$${originalApp.location.cost}` : null} />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <DetailItem label="Work Description" value={originalApp.location?.description} />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Contractor Details */}
              {originalApp.contractors && originalApp.contractors.length > 0 && (
                <Card className="shadow-sm border-0 mb-4 card-premium">
                  <Card.Header className="bg-white border-bottom py-3">
                    <h5 className="mb-0 fw-semibold text-dark">Contractor Details</h5>
                  </Card.Header>
                  <Card.Body className="p-4">
                    {originalApp.contractors.map((contractor, index) => (
                      <div key={index} className={index > 0 ? "mt-4 pt-3 border-top" : ""}>
                        {originalApp.contractors.length > 1 && <h6 className="text-secondary fw-bold mb-3">Contractor #{index + 1}</h6>}
                        <Row>
                          <Col md={6}>
                            <DetailItem label="Contractor Name" value={contractor.individualName} />
                          </Col>
                          <Col md={6}>
                            <DetailItem label="Trade" value={contractor.trade} />
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <DetailItem label="Email" value={contractor.email} />
                          </Col>
                          <Col md={6}>
                            <DetailItem label="Phone Number" value={contractor.phoneNumber} />
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12}>
                            <DetailItem label="Address" value={contractor.address} />
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <DetailItem label="License Number" value={contractor.license ? (Array.isArray(contractor.license) ? contractor.license.join(', ') : contractor.license) : null} />
                          </Col>
                          <Col md={6}>
                            <span className="small text-muted d-block mb-1 text-uppercase fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                              License Documents
                            </span>
                            <FileLinkList
                              files={dedupeFiles(normalizeFiles(contractor.licenseLink))}
                              icon={faFileLines}
                              emptyText="No license document uploaded."
                            />
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              )}
            </>
          )}

          {/* Plan Review (Check) Layout */}
          {product === "check" && (
            <Card className="shadow-sm border-0 mb-4 card-premium">
              <Card.Header className="bg-white border-bottom py-3">
                <h5 className="mb-0 fw-semibold text-dark">Request Details</h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row>
                  <Col md={6}>
                    <DetailItem label="Full Name" value={originalApp.name} />
                  </Col>
                  <Col md={6}>
                    <DetailItem label="Phone Number" value={originalApp.phoneNumber} />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <DetailItem label="Email Address" value={originalApp.email} />
                  </Col>
                  <Col md={6}>
                    <DetailItem label="Street Address" value={originalApp.address} />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <DetailItem label="Comments" value={originalApp.comments} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Inspection (Spect) Layout */}
          {product === "spect" && (
            <Card className="shadow-sm border-0 mb-4 card-premium">
              <Card.Header className="bg-white border-bottom py-3">
                <h5 className="mb-0 fw-semibold text-dark">Request Details</h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row>
                  <Col md={6}>
                    <DetailItem label="Full Name" value={originalApp.name} />
                  </Col>
                  <Col md={6}>
                    <DetailItem label="Phone Number" value={originalApp.phoneNumber} />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <DetailItem label="Email Address" value={originalApp.email} />
                  </Col>
                  <Col md={6}>
                    <DetailItem label="Street Address" value={originalApp.address} />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <DetailItem label="Requested Inspection Type" value={originalApp.requestedInspection} />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <DetailItem label="Comments" value={originalApp.comments} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}
        </Col>

        {/* Info & Admin Notes Panel */}
        <Col lg={4}>
          {/* Reviewer / Status details */}
          <Card className="shadow-sm border-0 mb-4 card-premium">
            <Card.Header className="bg-white border-bottom py-3">
              <h5 className="mb-0 fw-semibold text-dark">Review Details</h5>
            </Card.Header>
            <Card.Body className="p-4">
              <div className="mb-3">
                <span className="small text-muted d-block mb-1">Assigned Community Email</span>
                <span className="fw-semibold text-dark fs-6">{originalApp.communityEmail || originalApp.communityEmailAddress || "None Assigned"}</span>
              </div>
              <hr />
              <div className="mb-3">
                <span className="small text-muted d-block mb-1">Admin Feedback / Review Comments</span>
                <p className="text-secondary small bg-light p-3 rounded mb-0" style={{ whiteSpace: "pre-line", minHeight: "80px" }}>
                  {originalApp.adminComment || originalApp.reviewerComments || originalApp.reason || "No review comments provided yet."}
                </p>
              </div>
            </Card.Body>
          </Card>

          {/* Pricing Info (Permit only) */}
          {product === "permit" && (
            <Card className="shadow-sm border-0 mb-4 card-premium">
              <Card.Header className="bg-white border-bottom py-3">
                <h5 className="mb-0 fw-semibold text-dark">Fees Summary</h5>
              </Card.Header>
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted small">Permit Fee:</span>
                  <span className="fw-semibold text-dark">{formatMoney(originalApp.permitFee)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted small">Plan Review Fee:</span>
                  <span className="fw-semibold text-dark">{formatMoney(originalApp.planReviewFee)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted small">Inspections Fee:</span>
                  <span className="fw-semibold text-dark">{formatMoney(originalApp.inspectionsFee)}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-dark fw-bold">Total Fees:</span>
                  <span className="text-primary fw-bold fs-5">
                    {formatMoney(
                      parseMoney(originalApp.permitFee) +
                      parseMoney(originalApp.planReviewFee) +
                      parseMoney(originalApp.inspectionsFee)
                    )}
                  </span>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Documents the applicant submitted — shown for permit, check and spect */}
          <Card className="shadow-sm border-0 mb-4 card-premium">
            <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-semibold text-dark">Submitted Documents</h5>
              {submittedFiles.length > 0 && (
                <Badge bg="light" text="dark" className="border">{submittedFiles.length}</Badge>
              )}
            </Card.Header>
            <Card.Body className="p-4">
              <FileLinkList
                files={submittedFiles}
                icon={faFileLines}
                emptyText="No documents were uploaded with this application."
              />
            </Card.Body>
          </Card>

          {/* Reports and Files Download */}
          {reports.length > 0 && (
            <Card className="shadow-sm border-0 mb-4 card-premium">
              <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-semibold text-dark">Reports & Files</h5>
                <Badge bg="light" text="dark" className="border">{reports.length}</Badge>
              </Card.Header>
              <Card.Body className="p-4">
                <FileLinkList files={reports} icon={faFilePdf} />
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default PortalAppDetails;
